import React from 'react';
import { motion } from "framer-motion";
import DOMPurify from 'dompurify';
import { Calendar as CalendarIcon, MapPin, Clock, Info } from 'lucide-react';

const UpComingEvent = ({ events }) => {
  const now = new Date();
  const upcomingEvent = events.find(event => 
    new Date(event.start.dateTime || event.start.date) > now
  );

  if (!upcomingEvent) {
    return (
      <motion.div 
        className="bg-white rounded-xl shadow-md p-8 mb-12 text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-lightTeal flex items-center justify-center">
          <Info size={32} className="text-primary" />
        </div>
        <h3 className="font-display text-h3 text-secondary mb-4">No Upcoming Events</h3>
        <p className="text-bodyRegular text-textPrimary max-w-2xl mx-auto">
          Future events will all be posted on the calendar below. At the moment,
          there are no volunteer events scheduled, but check back later
          for any future updates.
        </p>
        <div className="w-16 h-1 bg-cta mx-auto mt-6"></div>
      </motion.div>
    );
  }

  const eventStartDate = new Date(upcomingEvent.start.dateTime || upcomingEvent.start.date);
  const eventEndDate = new Date(upcomingEvent.end.dateTime || upcomingEvent.end.date);
  const formattedDate = eventStartDate.toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const formattedTime = upcomingEvent.start.dateTime ? 
    `${eventStartDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - 
     ${eventEndDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}` : 
    "All Day";

  const addBlankTarget = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const links = doc.querySelectorAll('a');

    links.forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      // Add styling to links
      link.style.color = '#FF9B71'; // coral color
      link.style.textDecoration = 'underline';
    });

    return doc.body.innerHTML;
  };

  const eventDescription = upcomingEvent.description 
    ? addBlankTarget(upcomingEvent.description) 
    : "No additional details available at the moment.";

  const cleanDescription = DOMPurify.sanitize(eventDescription);

  return (
    <motion.div 
      className="bg-white rounded-xl shadow-lg overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="bg-primary p-4 flex items-center">
        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mr-4">
          <CalendarIcon className="text-primary" size={24} />
        </div>
        <h3 className="font-display text-h3 text-white">Upcoming Event</h3>
      </div>
      
      <div className="p-6 md:p-8">
        <h2 className="font-display text-h2 text-secondary mb-6">{upcomingEvent.summary}</h2>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-start">
            <CalendarIcon size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-textPrimary font-bold">Date</p>
              <p>{formattedDate}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <Clock size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-textPrimary font-bold">Time</p>
              <p>{formattedTime}</p>
            </div>
          </div>
          
          {upcomingEvent.location && (
            <div className="flex items-start">
              <MapPin size={20} className="text-primary mr-3 mt-1 flex-shrink-0" />
              <div>
                <p className="text-textPrimary font-bold">Location</p>
                <p>{upcomingEvent.location}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="w-full h-px bg-lightTeal my-6"></div>
        
        <div>
          <h4 className="font-display text-lg text-secondary mb-3">Event Details</h4>
          <div 
            className="text-textPrimary prose max-w-none"
            dangerouslySetInnerHTML={{ __html: cleanDescription }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default UpComingEvent;